import React, { useId, type ReactNode } from 'react';
import { Input, Readout, Root } from './Slider.styled';

export interface SliderProps {
  label: ReactNode;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
  /** Shown at the end of the row; the raw value when omitted. */
  formatValue?: (value: number) => ReactNode;
  disabled?: boolean;
  dataTestId?: string;
}

const fillPercent = (value: number, min: number, max: number): number =>
  max > min ? ((Math.min(Math.max(value, min), max) - min) / (max - min)) * 100 : 0;

/** A labelled range input with its value read out beside it. */
const Slider: React.FC<SliderProps> = ({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
  formatValue,
  disabled = false,
  dataTestId = 'slider',
}) => {
  const labelId = useId();
  return (
    <Root data-testid={dataTestId}>
      <span id={labelId}>{label}</span>
      <Input
        type="range"
        aria-labelledby={labelId}
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        $fill={fillPercent(value, min, max)}
        onChange={(event) => onChange(Number(event.target.value))}
        data-testid={`${dataTestId}__input`}
      />
      <Readout>{formatValue ? formatValue(value) : value}</Readout>
    </Root>
  );
};

export default Slider;
